import externalSites from "@/constants/externalSites"
import { SITE_ORIGIN } from "@/helpers/seo"

/**
 * schema.org JSON-LD builders.
 *
 * Emitted through vue-meta, which serialises `script: [{ type, json }]` and
 * removes the tag again on navigation — see the `json` content attribute in
 * vue-meta's renderTag.
 *
 * Rule for anything added here: every value must come from data the page really
 * renders. Google treats structured data that contradicts the visible page as
 * spam, and the penalty is worse than having no markup at all. That is why there
 * is no BreadcrumbList (the pages show no breadcrumb trail) and no SearchAction
 * (there is no search endpoint to point it at).
 */

const SCHEMA_CONTEXT = "https://schema.org"

const AVAILABILITY = {
  IN_STOCK: "https://schema.org/InStock",
  SOLD_OUT: "https://schema.org/SoldOut"
}

/**
 * @param {object} json
 * @returns {{ type: string, json: object }} a vue-meta script entry
 */
const asJsonLd = (json) => ({ type: "application/ld+json", json })

/**
 * Identifies GORO as an entity and links its verified profiles, which is what
 * lets search engines connect the site to them.
 * @returns {{ type: string, json: object }}
 */
export const organizationJsonLd = ({ description }) =>
  asJsonLd({
    "@context": SCHEMA_CONTEXT,
    "@type": "Organization",
    name: "GORO",
    url: `${SITE_ORIGIN}/`,
    // The only logo served from our own origin. It is 96x96, below the 112px
    // Google prefers for the logo rich result — worth hosting a larger one.
    logo: `${SITE_ORIGIN}/favicon.png`,
    description,
    sameAs: [
      externalSites.SOCIAL_MEDIA.FACEBOOK,
      externalSites.SOCIAL_MEDIA.INSTAGRAM,
      externalSites.SOCIAL_MEDIA.LINKEDIN,
      externalSites.SOCIAL_MEDIA.TIKTOK,
      externalSites.SOCIAL_MEDIA.YOUTUBE
    ]
  })

/**
 * A property listing. Modelled as Product + Offer rather than RealEstateListing:
 * what is on sale is a fractional token, not the whole building.
 *
 * `price` is the IDR price per token even on the `/en` variant, where the UI
 * converts to USD for display. IDR is the currency the purchase actually settles
 * in (see the FOOTER.TOKEN_PURCHASE_NOTE disclaimer), so it is the honest value.
 *
 * @param {{ name: string, description: string, url: string, images: string[],
 *           pricePerToken: number|string, isSoldOut: boolean,
 *           address?: string }} params
 * @returns {{ type: string, json: object }}
 */
export const propertyJsonLd = ({
  name,
  description,
  url,
  images,
  pricePerToken,
  isSoldOut,
  address
}) => {
  const json = {
    "@context": SCHEMA_CONTEXT,
    "@type": "Product",
    name,
    description,
    url
  }

  if (images && images.length) {
    json.image = images
  }

  // Omitted rather than guessed when the API has not supplied a price: an Offer
  // without a price is invalid, and a zero would misstate the listing.
  if (pricePerToken) {
    json.offers = {
      "@type": "Offer",
      url,
      price: pricePerToken,
      priceCurrency: "IDR",
      availability: isSoldOut ? AVAILABILITY.SOLD_OUT : AVAILABILITY.IN_STOCK
    }
  }

  if (address) {
    json.additionalProperty = [
      {
        "@type": "PropertyValue",
        name: "address",
        value: address
      }
    ]
  }

  return json.offers || json.image ? asJsonLd(json) : null
}
